import { Upload, type UploadProps } from 'antd';
import { Image } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import classes from './index.module.scss';
import type { UploadFile } from 'antd/lib';
import { getBase64, getFileName } from './scripts/utils';
import { useRefs } from '@/scripts/hooks';

export type MyUploadProps = Omit<UploadProps, 'onChange'> &
  (
    | {
        maxCount?: 1;
        stringValue?: true;
        value?: string;
        onChange?: (value: string) => void;
      }
    | {
        maxCount: number;
        stringValue: false;
        value?: string[];
        onChange?: (value: string[]) => void;
      }
  );

export default function MyUpload({
  className,
  accept,
  listType = 'picture-card',
  maxCount = 1,
  stringValue = true,
  value,
  onChange,
  children,
  ...props
}: MyUploadProps) {
  const { t } = useTranslation();
  const isStringValue = useMemo(
    () => stringValue && maxCount === 1,
    [stringValue, maxCount]
  );
  const isImage = useMemo(() => listType.includes('picture'), [listType]);
  const accept_ = useMemo(() => {
    if (accept) {
      return accept;
    }
    if (isImage) {
      return 'image/*';
    }
  }, [accept, isImage]);

  const children_ = useMemo(() => {
    if (children !== undefined) {
      return children;
    }
    switch (listType) {
      case 'picture-card':
      case 'picture-circle':
        return <PlusOutlined className={classes.icon_upload} />;
      default:
        return (
          <Button icon={<UploadOutlined></UploadOutlined>}>{t('action.upload')}</Button>
        );
    }
  }, [children, listType, t]);

  const refEmitValue = useRef<any>();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string>();

  const value_ = useDeferredValue(
    useMemo(() => {
      let value: string | string[] = [];
      for (const item of fileList) {
        if (item.status === 'done') {
          const url = item.url || item.response?.name;
          if (url) {
            value.push(url);
          }
        }
      }
      if (isStringValue) {
        value = value[0];
      }
      return value;
    }, [fileList, isStringValue])
  );

  const refs = useRefs({
    fileList,
    onChange,
  });

  useEffect(() => {
    if (value !== refEmitValue.current) {
      const fileList: UploadFile[] = [value!]
        .flat()
        .filter(Boolean)
        .map((url) => ({
          uid: url,
          name: getFileName(url),
          status: 'done',
          url: url,
        }));
      setFileList(fileList);
    }
  }, [refs, value]);

  useUpdateEffect(() => {
    const { onChange, fileList } = refs.current;
    if (fileList.every((item) => item.status !== 'uploading')) {
      refEmitValue.current = value_;
      onChange?.(value_ as any);
    }
  }, [refs, value_]);

  return (
    <>
      <Upload
        className={classNames(classes.upload, className, listType, {
          [classes.maxed]: fileList.length >= maxCount,
        })}
        accept={accept_}
        fileList={fileList}
        listType={listType}
        maxCount={maxCount}
        // TODO amo: maxSize typeCheck
        // beforeUpload={}
        // customRequest={async ({ file, onError, onSuccess, onProgress }) => {
        //   if (!(file instanceof File)) {
        //     onError?.(new Error('Abort upload'));
        //     return;
        //   }
        //   try {
        //     const data = await uploadApis.uploadFile(file, (e) => {
        //       onProgress?.(
        //         Object.assign(e, {
        //           percent: e.progress,
        //         })
        //       );
        //     });
        //     onSuccess?.(data);
        //   } catch (error) {
        //     onError?.(error as Error);
        //   }
        // }}
        onChange={(info) => {
          setFileList(info.fileList);
        }}
        onPreview={async (file) => {
          let url: string = file.url || file.response?.name;
          if (isImage) {
            if (!url && file.originFileObj) {
              url = await getBase64(file.originFileObj as File);
            }
            if (url) {
              setPreviewUrl(url);
            }
          } else {
            // TODO amo: Not image preview
            if (url) {
              window.open(url, '_blank');
            }
          }
        }}
        {...props}
      >
        {children_}
      </Upload>
      {isImage && previewUrl && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: Boolean(previewUrl),
            onVisibleChange: (visible) => {
              if (!visible) {
                setPreviewUrl(undefined);
              }
            },
          }}
          src={previewUrl}
        />
      )}
    </>
  );
}
